import { useAuth } from "@/context/auth-context";
import { action, computed, makeAutoObservable, observable } from "mobx";
import { useLocalObservable } from "mobx-react-lite";
import { createContext, useContext, useEffect } from "react";
import { useProviders } from "@/queries/providers";
import { CustomProvider } from "@/queries/providers/types";
import { observer } from "mobx-react-lite";

export class LLMProviderStore {
  @observable _selectedProviderId: number | undefined = undefined;
  @observable _providers: CustomProvider[] = [];
  @observable _providersLoading: boolean = false;
  @observable _providersPage: number = 1;
  @observable _providersPageSize: number = 250;

  constructor() {
    makeAutoObservable(this);
  }

  @action setSelectedProviderId = (providerId: number | undefined) => {
    this._selectedProviderId = providerId;
  };

  @action setProviders = (providers: CustomProvider[]) => {
    this._providers = providers;
  };

  @action setProvidersLoading = (providersLoading: boolean) => {
    this._providersLoading = providersLoading;
  };

  @action setProvidersPageSize = (providersPageSize: number) => {
    this._providersPageSize = providersPageSize;
  };

  @computed get customProviders() {
    return this._providers.filter(
      (provider) => provider.workspace_id !== undefined,
    );
  }

  @computed get selectedProvider() {
    return this._providers.find((p) => p.id === this._selectedProviderId);
  }

  @computed get providersPage() {
    return this._providersPage;
  }

  @computed get providersPageSize() {
    return this._providersPageSize;
  }

  @computed get providersLoading() {
    return this._providersLoading;
  }

  @computed get selectedProviderId() {
    return this._selectedProviderId;
  }

  @computed get providers() {
    return this._providers;
  }

  @action reset = () => {
    this._selectedProviderId = undefined;
    this._providers = [];
  };
}

export interface LLMProviderContextType {
  store: LLMProviderStore;
  setProvidersPage: React.Dispatch<React.SetStateAction<number>>;
}

export const LLMProviderContext = createContext<
  LLMProviderContextType | undefined
>(undefined);

export const LLMProviderStoreProvider = observer(
  ({ children }: { children: React.ReactNode }) => {
    const store = useLocalObservable(() => new LLMProviderStore());
    const auth = useAuth();
    const userToken = auth!.userToken;

    const {
      data: providers,
      isLoading: providersLoading,
      setPage: setProvidersPage,
    } = useProviders({
      userToken: userToken!,
      enabled: !!userToken,
    });

    useEffect(() => {
      if (providers) {
        store.setProviders(providers);
      }
    }, [providers, store]);

    useEffect(() => {
      store.setProvidersLoading(providersLoading);
    }, [providersLoading, store]);

    return (
      <LLMProviderContext.Provider
        value={{
          store,
          setProvidersPage,
        }}
      >
        {children}
      </LLMProviderContext.Provider>
    );
  },
);

export const useLLMProviderStore = (): LLMProviderContextType => {
  const context = useContext(LLMProviderContext);
  if (!context) {
    throw new Error(
      "useLLMProviderStore must be used within a LLMProviderStoreProvider",
    );
  }
  return context;
};
